/**
 * Show entity class
 */
import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'
import { showLocalizationPairs, metadataMapToLocalizations, localizationsToMetadataMap } from 'app/helpers/localization'

// TV Show localized metadata: backend verticalImage/horizontalImage map to the
// model's portraitImageUrl (vertical) / backgroundImageUrl (horizontal) fields.
const SHOW_LOCALIZATION_PAIRS = showLocalizationPairs('portraitImageUrl', 'backgroundImageUrl')

/**
 * ShowFactory
 * returns Show entity class
 *
 * @class ShowFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Show} class
 *
 * @ngdoc factory
 * @name mwApp.factory:ShowFactory
 */
/* @ngInject */
export default function ShowFactory ($http, $injector, Entity, Type, onOffMixin, FileUploadMixin, Network, DefaultStream, UrlPolicy, ShowSet, m2mMixin, alertLiteSvc, authSvc) {
  /* === Show class === */

  /**
   * Show model
   *
   * @class Show
   * @classdesc Show model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @return {models.Show} Show entity
   */
  function Show () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Show.prototype = Object.create(Entity.prototype)
  Show.prototype.constructor = Show

  /* === Show instance methods & fields === */

  Object.assign(Show.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Show#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      description: String,
      directors: String,
      stars: String,
      year: Number,
      $releasedDate: Date,
      $ageRatingType: 'default',
      $country: {
        id: Number,
        name: String,
        isoCode: String
      },
      $defaultAgeRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      ageRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      enabled: Boolean,
      portraitImageUrl: String,
      backgroundImageUrl: String,
      localizationMetadata: {}, // map<langCode, {name,description,directors,stars,horizontalImage,verticalImage}>
      addedTime: Date,
      dolbyAtmosAvailable: Boolean,
      resolution: 'HD',
      updatedTime: Date,
      episodeCount: Number,
      seasonCount: Number,
      locked: false,
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreams',
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      policyAssignedToTvShowOrEpisodes: Boolean,
      // listed: Number,
      networks: Type.Stump(['Network'], ['id']),
      urls: {
        STB: 'MediaUrlDTO',
        PC: 'MediaUrlDTO',
        iOS: 'MediaUrlDTO',
        Android: 'MediaUrlDTO',
        SamsungTV: 'MediaUrlDTO',
        Others: 'MediaUrlDTO',
        WEB: 'MediaUrlDTO'
      },
      $scheduledPublishing: Boolean,
      publishTime: String,
      unpublishTime: String,
      categories: [],
      acl: {
        id: Number,
        name: String
      },
      tags: [{ name: String }],
      contentSets: ['ShowSet'],
      assignedSubscriptions: ['Subscription'],
      vertical: Boolean,
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $toDateUnlimited: true,
      $geoBlockingEnabled: false,
      $lastUpload: 0,
      $itemType: 'show',
      $contentSetType: 'showSet',
      $contentSetName: 'Content Set'
    },

    /**
     * url template
     *
     * @memberof models.Show#
     * @type {String}
     */
    _urlTemplate: 'api/tvshows/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Show#
     * @type {String}
     */
    _publicUrlTemplate: 'shows/{{::id}}',

    /**
     * Prepare object
     *
     * @memberof models.Show#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      /* Note: set default values. */
      const date = new Date()
      this.$releasedDate = this.year
        ? date.setFullYear(this.year)
        : date

      this.$geoBlockingEnabled = !!this.acl
      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      if (this.ageRating) {
        if (!this.ageRating.country) {
          this.$ageRatingType = 'default'
          this.$defaultAgeRating = this.ageRating
          this.ageRating = {}
        } else {
          this.$ageRatingType = 'countryBased'
          this.$country = this.ageRating.country
          this.$defaultAgeRating = {}
        }
      }

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime
      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')

      // Build the directive-friendly localizations array from the backend map.
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, SHOW_LOCALIZATION_PAIRS)
    },

    /**
     * Get underlying object
     *
     * @memberof models.Show#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      const releasedDate = new Date(this.$releasedDate)
      this.year = releasedDate.getFullYear()
      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.acl = this.$geoBlockingEnabled ? this.acl : null
      this.ageRating = this.$ageRatingType === 'default' ? this.$defaultAgeRating : this.ageRating
      if (this.ageRating) {
        delete this.ageRating.country
      }

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }

      // delete null urls
      if (this.urls) {
        this.urls =
          Object.keys(this.urls)
            .reduce(
              (o, platform) => {
                if (this.urls[platform] != null) {
                  o[platform] = this.urls[platform]
                }
                return o
              },
              Object.create(null)
            )
      }

      // Serialize the localizations array back into the backend map shape.
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, SHOW_LOCALIZATION_PAIRS)

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.Show#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}') !== -1
          ? this[fieldName].replace('{{size}}', size) + '?lastUpload=' + this.$lastUpload
          : this[fieldName] + '?resize=' + size + '&lastUpload=' + this.$lastUpload
        : ''
    }

  }, onOffMixin, FileUploadMixin, m2mMixin('networks'), m2mMixin('categories'), m2mMixin('tags'))

  /* === Show static methods & fields === */

  /**
   * Move episode to VOD
   *
   * @param {Object} params
   * @param {Number} params.id
   * @param {Object} params.tvShow
   * @param {Number} params.tvShow.id
   * @param {Object} params.season
   * @param {Number} params.season.id
   * @param params.isWithDelete
   * @return {Promise} promise
   */
  Show.moveToVOD = function (params) {
    const { id, tvShow, season, isWithDelete } = params

    return $http.post(`api/tvshows/${tvShow.id}/moveToVod`, { seasonId: season ? season.id : null, episodeId: id, isWithDelete })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Order episode by show
   *
   * @param {Number} sortOrder
   * @param {Object} episode
   * @return {Promise} promise
   */
  Show.orderEpisode = function (sortOrder, episode) {
    const { _url, season } = episode

    return $http.patch(_url + '/order', { sortOrder, seasonId: season ? season.id : null })
      .catch(
        rejection => {
          return alertLiteSvc.alertError(rejection)
        }
      )
  }

  // TODO: use default method for extra!
  Show._loadExtra = function () {
    return $http.get('/nora/api/tvshows/extra/categories')
  }

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  Show.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Search shows by param
   *
   * @param {String} search
   * @returns {Promise} promise
   */
  Show.searchShows = function (search) {
    return $http.post(Show.generateUrl('extra/tvshows'), { searchStr: search })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Search VODs by param
   *
   * @param {String} search
   * @returns {Promise} promise
   */
  Show.searchVODs = function (search) {
    return $http.post(Show.generateUrl('extra/vods'), { searchStr: search })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Search tags by param
   *
   * @param {String} url
   * @param {Object} params
   * @returns {Promise} promise
   */
  Show.search = function (url, params) {
    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Load Content sets
   *
   * @param show
   * @returns {*}
   */
  Show.loadAvailableContentSets = function (show) {
    return $http.get(`/nora/api/tvshows/${show.id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(Show, Entity)
  Show._name = 'Show'
  Show.init()

  // permissions
  Show.access = {
    view: 'viewVodSeries',
    create: 'createVodSeries',
    edit: 'editVodSeries',
    delete: 'deleteVodSeries'
  }

  return Show
}
